"use client";

import { memo, useState, useEffect, useRef } from "react";
import Link from "next/link";

import Logo from "@/components/Logo";
import { HamburgerIcon, SearchIcon } from "@/components/ui/Icons";
import { useReduxAuth } from "@/hooks/useReduxAuth";
import { useAuth } from "@/contexts/AuthContext";
import { COMPANY_LINKS } from "@/contants/HeaderContants";
import CategoryMegaMenu from "@/components/nav/CategoryMegaMenu";
import PrimaryNavStrip from "@/components/nav/PrimaryNavStrip";
import HeaderSearch from "@/components/nav/HeaderSearch";
import { getAllTagsFromStored } from "@/lib/newsTags";

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
    const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);
    const desktopSearchRef = useRef(null);
    const { user, logout } = useReduxAuth();
    const { navCategories, navPreview } = useAuth();
    const navItems = navCategories;

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (!isMenuOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isMenuOpen]);

    useEffect(() => {
        if (!isSearchOpen) return;
        const onPointerDown = (event) => {
            if (desktopSearchRef.current?.contains(event.target)) return;
            if (event.target.closest?.(".header-search-panel")) return;
            setIsSearchOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setIsSearchOpen(false);
        };
        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isSearchOpen]);

    const toggleMenu = () => setIsMenuOpen((v) => !v);
    const toggleSearch = () => setIsSearchOpen((v) => !v);
    const clearCategoryHover = () => setHoveredCategoryId(null);

    return (
        <div className={`transition-shadow ${isScrolled ? "shadow-lg" : "shadow-sm"}`}>
            {/* Mobile header */}
            <header className="lg:hidden site-header">
                <div className="site-header__masthead">
                <div className="site-container h-14 flex items-center justify-between gap-2 min-w-0">
                    <div className="flex items-center gap-1 min-w-0">
                        <button
                            type="button"
                            className="text-white flex items-center justify-center cursor-pointer shrink-0 w-11 h-11 -ml-1"
                            aria-label="Menu"
                            onClick={toggleMenu}
                        >
                            <HamburgerIcon size={22} />
                        </button>
                        <Link href="/" className="shrink-0 min-w-0">
                            <Logo inverted />
                        </Link>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            className="text-white flex items-center justify-center cursor-pointer w-11 h-11"
                            aria-label="Search"
                            aria-expanded={isSearchOpen}
                            onClick={toggleSearch}
                        >
                            <SearchIcon size={20} />
                        </button>
                        {user ? (
                            <div className="dropdown dropdown-end">
                                <div tabIndex={0} role="button" className="flex items-center justify-center w-10 h-10 bg-(--brand-accent) text-[#1a1a1a] rounded-full font-bold text-[13px] cursor-pointer hover:opacity-90 transition">
                                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                                </div>
                                <ul tabIndex={0} className="dropdown-content menu z-200 w-56 max-w-[calc(100vw-2rem)] bg-base-100 text-base-content rounded-lg shadow-lg py-2 mt-2">
                                    <li className="px-4 py-2 border-b border-base-200">
                                        <p className="text-sm font-semibold pointer-events-none">{user.name}</p>
                                        <p className="text-xs text-base-content/60 pointer-events-none">{user.email}</p>
                                    </li>
                                    <li>
                                        <button onClick={logout} className="w-full text-left px-4 py-2 text-sm transition hover:bg-error hover:text-white">
                                            Sign out
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        ) : null}
                    </div>
                </div>
                </div>

            </header>

            {/* Mobile side drawer */}
            <div
                className={`lg:hidden fixed inset-0 z-120 transition duration-200 ${isMenuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
                onClick={() => setIsMenuOpen(false)}
            >
                <div className={`absolute inset-0 bg-black/40 transition-opacity ${isMenuOpen ? "opacity-100" : "opacity-0"}`} />
                <nav
                    className={`absolute left-0 z-130 top-0 h-full w-72 max-w-[85vw] bg-white text-(--text-primary) shadow-xl transition-transform duration-200 ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-(--border-default)">
                        <Logo />
                        <button
                            type="button"
                            className="text-(--text-secondary) text-sm min-h-10 min-w-10 px-2"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            बंद
                        </button>
                    </div>

                    <div className="p-4 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
                        <p className="mr m-0 mb-3 text-[11px] font-bold uppercase tracking-widest text-(--text-tertiary)">
                            विभाग
                        </p>
                        <div className="space-y-1">
                        {navItems.map((item) => {
                            const isOpen = expandedMobileCategory === item.id;
                            const preview = navPreview[item.id] || [];
                            return (
                                <div key={item.id} className="border-b border-(--border-default) last:border-0 pb-2 last:pb-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <Link
                                            href={item.link}
                                            className="mr block py-2.5 text-sm font-semibold text-(--text-primary) min-w-0"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            {item.name}
                                        </Link>
                                        {preview.length > 0 && (
                                            <button
                                                type="button"
                                                className="text-xs font-semibold text-(--brand-primary) min-h-10 min-w-10 px-2"
                                                onClick={() => setExpandedMobileCategory(isOpen ? null : item.id)}
                                                aria-expanded={isOpen}
                                            >
                                                {isOpen ? "▴" : "▾"}
                                            </button>
                                        )}
                                    </div>
                                    {isOpen && preview.length > 0 && (
                                        <ul className="m-0 p-0 list-none space-y-2 pb-2 pl-1">
                                            {preview.slice(0, 3).map((news) => (
                                                <li key={news.id}>
                                                    <Link
                                                        href={`/news/${news.slug}`}
                                                        className="mr block text-xs font-medium text-(--text-secondary) leading-snug hover:text-(--brand-primary) py-1"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        {news.title}
                                                    </Link>
                                                    {getAllTagsFromStored(news.tags).length > 0 && (
                                                        <div className="mt-1 flex flex-wrap gap-1 pl-0.5">
                                                            {getAllTagsFromStored(news.tags).slice(0, 2).map((tag) => (
                                                                <span
                                                                    key={`${news.id}-${tag}`}
                                                                    className="mr text-[10px] text-(--text-tertiary)"
                                                                >
                                                                    #{tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                        <div className="pt-4 mt-4 border-t border-(--border-default)">
                            <div className="mr text-[11px] font-bold uppercase text-(--text-tertiary) mb-2">Company</div>
                            <div className="space-y-1">
                                {COMPANY_LINKS.filter((l) => !l.divider).map((l) => (
                                    <Link key={l.href} href={l.href} className="block mr text-sm text-(--text-secondary) py-2" onClick={() => setIsMenuOpen(false)}>
                                        {l.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </nav>
            </div>

            {/* Desktop header */}
            <header className="site-header">
                <div className="relative" onMouseLeave={clearCategoryHover}>
                    <div className="hidden lg:block site-header__masthead">
                    <div className="site-container header-top">
                        <Link href="/" className="header-top__logo-link">
                            <Logo big inverted />
                        </Link>

                        <div className="header-top__end">
                            <div className="header-top__actions">
                                <div className="header-top__search-wrap" ref={desktopSearchRef}>
                                    <button
                                        type="button"
                                        className="header-top__search-btn"
                                        aria-label="Search"
                                        aria-expanded={isSearchOpen}
                                        onClick={toggleSearch}
                                    >
                                        <SearchIcon size={18} />
                                    </button>
                                    {isSearchOpen && (
                                        <div className="header-search-popup" role="dialog" aria-label="Search">
                                            <HeaderSearch
                                                autoFocus
                                                onSubmitSuccess={() => setIsSearchOpen(false)}
                                            />
                                        </div>
                                    )}
                                </div>
                                {user ? (
                                    <div className="dropdown dropdown-end">
                                        <div tabIndex={0} role="button" className="header-top__avatar">
                                            {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                                        </div>
                                        <ul tabIndex={0} className="dropdown-content menu z-200 w-56 bg-base-100 text-base-content rounded-lg shadow-lg py-2 mt-2">
                                            <li className="px-4 py-2 border-b border-base-200">
                                                <p className="text-sm font-semibold pointer-events-none">{user.name}</p>
                                                <p className="text-xs text-base-content/60 pointer-events-none">{user.email}</p>
                                            </li>
                                            <li>
                                                <button onClick={logout} className="w-full text-left px-4 py-2 text-sm transition hover:bg-error hover:text-white">
                                                    Sign out
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    </div>

                    <div className="site-bar site-bar--primary-nav">
                        <div className="site-container site-bar__inner">
                            <PrimaryNavStrip
                                categories={navItems}
                                activeCategoryId={hoveredCategoryId}
                                onCategoryHover={setHoveredCategoryId}
                                onCategoryLeave={clearCategoryHover}
                            />
                        </div>
                    </div>

                    <div className="hidden lg:block">
                        <CategoryMegaMenu
                            categories={navItems}
                            previewByCategoryId={navPreview}
                            activeCategoryId={hoveredCategoryId}
                            onHoverCategory={setHoveredCategoryId}
                            onLeave={clearCategoryHover}
                        />
                    </div>
                </div>

            </header>

            {/* Mobile search panel */}
            {isSearchOpen && (
                <div className="lg:hidden header-search-panel bg-white border-b border-(--border-default) shadow-md">
                    <div className="site-container py-3">
                        <HeaderSearch
                            variant="mobile"
                            autoFocus
                            onSubmitSuccess={() => setIsSearchOpen(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default memo(Header);
